import { Block, Delete, FilterAlt, TaskAlt } from "@mui/icons-material";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import DrawerCM from "components/shared/Drawer";
import generateHeadCells from "helper/generate-head-cell";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  StyledDateBox,
  StyledGridIcons,
  StyledMenuItem,
  StyledStack,
} from "./Attendance.style";

import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";
import DatePickerCM from "components/shared/DatePicker";
import { convertNum } from "helper/convert-num";
import { PAGINATION_LIMIT } from "constants";
import useHttp from "hooks/use-http";
import {
  getStudentAttendanceUrlRequest,
  getStudentClassesRequest,
} from "api/requests";
import { useEffect } from "react";
import { useCallback } from "react";
import LoadingCM from "components/shared/Loading";

const headCells = generateHeadCells("user_attendance");

export default function Attendance() {
  // filter option state
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedClass, setSelectedClass] = useState({
    value: null,
  });

  const [studentClasses, setStudentClasses] = useState({
    data: null,
    count: 0,
  });

  const [attendanceData, setAttendanceData] = useState({
    data: [{ content: "اطلاعاتی یافت نشد" }],
    count: 0,
  });

  // paginition state
  const [page, setPage] = useState(1);

  const { loading, request } = useHttp();

  const { register, control } = useForm();

  // date filter handler
  const onFromDateHandler = (value) =>
    setFromDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  const onToDateHandler = (value) =>
    setToDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  const getAttendance = async (filter = true) => {
    const query = {
      skip: (page - 1) * PAGINATION_LIMIT,
      limit: PAGINATION_LIMIT,
      class_id: selectedClass.value ?? null,
      from_date: fromDate ?? null,
      to_date: toDate ?? null,
    };

    const res = await request({
      url: getStudentAttendanceUrlRequest,
      values: filter ? query : { skip: 0, limit: PAGINATION_LIMIT },
    });

    if (!res.count || !res.data.length) {
      return setAttendanceData({
        data: [{ content: "اطلاعاتی یافت نشد" }],
        count: 0,
      });
    }

    const transformedData = res.data.map((item) => {
      return {
        id: item.id,
        className: item.attendance.class.title,
        courceName: item.attendance.class.course.title,
        educational_year: item.attendance.class.educational_year,
        status: item.is_present ? (
          <TaskAlt color="success" />
        ) : (
          <Block color="error" />
        ),
        date: item.attendance.date.string ?? "ثبت نشده",
      };
    });

    setAttendanceData({
      data: transformedData,
      count: res.count,
    });
  };

  const getWithFilter = () => {
    setPage(1);
    getAttendance(true);
  };

  const getWithoutFilter = () => {
    setPage(1);
    setFromDate("");
    setToDate("");
    setSelectedClass("");
    getAttendance(false);
  };

  const getClasses = useCallback(async () => {
    const { data: res } = await request({
      url: getStudentClassesRequest,
    });
    if (res.count || res.data.length) {
      const transformedData = res.data.map((item) => ({
        id: item.id,
        name: item.title,
      }));

      setStudentClasses({
        data: transformedData,
        count: res.count,
      });
    }
  }, []);

  useEffect(() => {
    getClasses();
    getAttendance();
  }, [page, getClasses]);
  return (
    <DrawerCM title="حضور و غیاب دانش آموز" $styledHeader>
      {loading ? (
        <LoadingCM />
      ) : (
        <>
          <StyledStack mt={4}>
            <Grid container>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select-student_class"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="student_class">انتخاب کلاس</InputLabel>
                      <Select
                        id="student_class"
                        name="student_class"
                        label="انتخاب کلاس"
                        labelId="select-student_class"
                        value={selectedClass?.value || ""}
                        {...register("student_class")}
                        onChange={(e) =>
                          setSelectedClass({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        {studentClasses?.data?.map((item, index) => {
                          return (
                            <StyledMenuItem key={index} value={item.id}>
                              {item.name}
                            </StyledMenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={4} xs={12} py={0.5} px={0.5}>
                <StyledDateBox>
                  <DatePickerCM
                    onChange={onFromDateHandler}
                    render={(value, openCalendar) => {
                      return (
                        <TextField
                          label="از تاریخ"
                          variant="outlined"
                          fullWidth
                          size="small"
                          id="from_created_at"
                          {...register("from_created_at")}
                          value={fromDate ? fromDate.split("-").join("/") : ""}
                          onClick={openCalendar}
                        />
                      );
                    }}
                  />
                </StyledDateBox>
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <StyledDateBox>
                  <DatePickerCM
                    onChange={onToDateHandler}
                    render={(value, openCalendar) => {
                      return (
                        <TextField
                          label="تا تاریخ"
                          variant="outlined"
                          fullWidth
                          size="small"
                          id="to_created_at"
                          {...register("to_created_at")}
                          value={toDate ? toDate.split("-").join("/") : ""}
                          onClick={openCalendar}
                        />
                      );
                    }}
                  />
                </StyledDateBox>
              </Grid>
              <StyledGridIcons item xs={12} p={1}>
                <IconButton color="primary" onClick={getWithFilter}>
                  <Tooltip title="اعمال فیلتر" placement="top">
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
                <IconButton color="error" onClick={getWithoutFilter}>
                  <Tooltip title="حذف فیلتر" placement="top">
                    <Delete />
                  </Tooltip>
                </IconButton>
              </StyledGridIcons>
            </Grid>
          </StyledStack>
          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                rows={attendanceData.data}
                page={page}
                headCells={headCells}
                colspan={attendanceData.count === 0 ? "10" : ""}
                fontSize="12px"
                cellFontSize="12px"
              />
              {attendanceData.data.length < 1 ? (
                ""
              ) : (
                <PaginationCM
                  handleChange={(_, val) => setPage(val)}
                  page={page}
                  count={attendanceData.count}
                />
              )}
            </Grid>
          </Grid>
        </>
      )}
    </DrawerCM>
  );
}
